import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Check, ArrowRight, MapPin } from "lucide-react";
import Seo, { serviceSchema, faqSchema } from "../components/site/Seo";
import Breadcrumbs from "../components/site/Breadcrumbs";
import PageHero from "../components/site/PageHero";
import Reveal from "../components/site/Reveal";
import FaqSection from "../components/site/FaqSection";
import FinalCTA from "../components/site/FinalCTA";
import { getService, SERVICES_DATA } from "../lib/services";
import { PRIMARY_LOCATIONS } from "../lib/locations";

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = getService(slug);
  if (!service) return <Navigate to="/services" replace />;

  const related = service.related.map(getService).filter(Boolean);

  return (
    <div>
      <Seo
        title={service.title}
        description={service.metaDescription}
        path={`/services/${service.slug}`}
        jsonLd={serviceSchema(service.name, service.metaDescription)}
        jsonLdId="service-schema"
      />
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: service.name, path: `/services/${service.slug}` }]} />

      <PageHero overline={`${service.category} Service`} title={service.name} subtitle={service.short} image={service.image} />

      {/* Overview + Benefits */}
      <section className="py-16 md:py-20 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-5 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 text-left">
            <span className="text-[#C69453] text-sm font-semibold tracking-[0.2em] uppercase">Overview</span>
            <h2 className="text-2xl md:text-3xl font-light text-white mt-2 mb-5">What You Get</h2>
            {service.overview.map((p, i) => (
              <p key={i} className="text-zinc-300 text-lg leading-relaxed mb-4">{p}</p>
            ))}

            <h3 className="text-xl font-semibold text-white mt-8 mb-3">Ideal Applications</h3>
            <div className="flex flex-wrap gap-2">
              {service.applications.map((a) => (
                <span key={a} className="border border-zinc-800 bg-[#141414] text-zinc-300 text-sm px-3 py-1.5">{a}</span>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="border border-zinc-800 bg-[#141414] p-7 relative">
              <div className="absolute top-0 left-0 w-full h-1 bg-[#C69453]" />
              <h3 className="text-xl font-semibold text-white mb-4">Key Benefits</h3>
              <ul className="space-y-3">
                {service.benefits.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-zinc-300">
                    <Check className="w-5 h-5 text-[#C69453] shrink-0 mt-0.5" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#C69453] hover:bg-[#AE7F44] text-black font-bold px-6 py-3.5 transition-colors">
                <span>Request Your Free Estimate</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-12 bg-zinc-950 border-y border-zinc-900">
        <div className="max-w-7xl mx-auto px-5 lg:px-12">
          <h2 className="text-2xl md:text-3xl font-light text-white mb-6">Recent {service.name} Projects</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {service.gallery.map((src, i) => (
              <Reveal key={i} delay={(i % 3) * 0.06}>
                <div className="overflow-hidden border border-zinc-800 aspect-[4/3]">
                  <img src={src} alt={`${service.name} project in Northern Arizona`} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </Reveal>
            ))}
          </div>
          <Link to="/gallery" className="inline-flex items-center gap-2 mt-6 text-[#C69453] font-semibold hover:gap-3 transition-all">
            <span>See the full gallery</span><ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <FaqSection faqs={service.faqs} className="bg-[#0A0A0A]" heading={`${service.name} — Questions`} />

      {/* Related services + cities (internal linking) */}
      <section className="py-16 bg-zinc-950 border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-5 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h3 className="text-xl font-semibold text-white mb-4">Related Services</h3>
            <div className="space-y-3">
              {related.map((r) => (
                <Link key={r.slug} to={`/services/${r.slug}`} className="flex items-center justify-between border border-zinc-800 bg-[#141414] p-4 hover:border-[#C69453] transition-colors group">
                  <div>
                    <p className="text-white font-semibold">{r.name}</p>
                    <p className="text-zinc-500 text-sm">{r.short}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#C69453] group-hover:translate-x-1 transition-transform" />
                </Link>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-white mb-4">Available In</h3>
            <div className="flex flex-wrap gap-2">
              {PRIMARY_LOCATIONS.map((c) => (
                <Link key={c.slug} to={`/service-areas/${c.slug}`} className="flex items-center gap-2 border border-zinc-800 bg-[#141414] text-zinc-300 px-4 py-2 hover:border-[#C69453] hover:text-white transition-colors">
                  <MapPin className="w-4 h-4 text-[#C69453]" />{c.name}
                </Link>
              ))}
              <Link to="/service-areas" className="flex items-center gap-2 border border-[#C69453] text-[#C69453] px-4 py-2 hover:bg-[#C69453]/10 transition-colors">
                All Service Areas
              </Link>
            </div>
          </div>
        </div>
      </section>

      <FinalCTA heading={`Ready for Your New ${service.name}?`} />
      <Seo jsonLd={faqSchema(service.faqs)} jsonLdId="service-faq-schema" path={`/services/${service.slug}`} />
    </div>
  );
};

export { SERVICES_DATA };
export default ServiceDetail;
